import { FunctionComponent, useState, useEffect, useCallback } from "react";
import { useAppSelector } from "./store/hooks";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { getDB } from "./Firebase";
import { doc, getDoc, collection, getDocs, addDoc, deleteDoc, serverTimestamp, orderBy, query } from "firebase/firestore";

// ── Типы ──────────────────────────────────────────────────────────────────────
type Tab = "dashboard" | "users" | "withdrawals" | "payments" | "games" | "config" | "tickets" | "keys" | "news";

interface AdminUser {
  id: string; nickname: string; coins: number; rating: number;
  level: number; gamesPlayed: number; createdAt: any; lastSeen: any;
  banned?: boolean; adminNote?: string;
}
interface Withdrawal {
  id: string; uid: string; nickname: string; amount: number;
  phone?: string; address?: string; network?: string; currency?: string;
  method?: string; status: string; createdAt: any;
}
interface Payment { id: string; uid: string; nickname: string; coins: number; processedAt: any; }
interface Ticket { id: string; uid: string; nickname: string; subject: string; body: string; status: string; createdAt: any; adminReply?: string; }
interface Config { commission: number; minWithdraw: number; maxBet: number; betOptions: number[]; startingCoins: number; defaultRating: number; kFactor: number; }
interface Stats { totalUsers: number; pendingWithdraws: number; totalPayments: number; }

// ── Получаем токен текущего пользователя ────────────────────────────────────
async function getToken(): Promise<string> {
  const user = getAuth().currentUser;
  if (!user) throw new Error("Not authenticated");
  return user.getIdToken();
}

async function adminFetch(path: string, options: RequestInit = {}) {
  const token = await getToken();
  const r = await fetch(`/admin${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(options.headers || {}) },
  });
  if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || r.statusText); }
  return r.json();
}

// ── Форматирование ─────────────────────────────────────────────────────────
function fmtDate(ts: any): string {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts._seconds * 1000);
  return d.toLocaleString("ru");
}

// ══════════════════════════════════════════════════════════════════════════════
//  DASHBOARD
// ══════════════════════════════════════════════════════════════════════════════
const Dashboard: FunctionComponent<{ stats: Stats | null }> = ({ stats }) => (
  <div className="Admin-dashboard">
    <h2 className="Admin-section-title">Обзор</h2>
    <div className="Admin-stats-grid">
      {[
        { label: "Пользователей", value: stats?.totalUsers ?? "…" },
        { label: "Заявок на вывод", value: stats?.pendingWithdraws ?? "…" },
        { label: "Обработано платежей", value: stats?.totalPayments ?? "…" },
      ].map((s) => (
        <div key={s.label} className="Admin-stat-card">
          <div className="Admin-stat-value">{s.value}</div>
          <div className="Admin-stat-label">{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
//  USERS
// ══════════════════════════════════════════════════════════════════════════════
const UsersTab: FunctionComponent = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editCoins, setEditCoins] = useState("");
  const [editRating, setEditRating] = useState("");
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await adminFetch(`/users?search=${search}&limit=100`)); } catch (e: any) { setMsg(e.message); }
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!editing) return;
    setSaving(true); setMsg("");
    try {
      await adminFetch(`/users/${editing.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          coins:     editCoins  !== "" ? Number(editCoins)  : undefined,
          rating:    editRating !== "" ? Number(editRating) : undefined,
          adminNote: editNote || undefined,
          banned:    editing.banned,
        }),
      });
      setMsg("✅ Сохранено"); setEditing(null); load();
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">Пользователи</h2>
      <div className="Admin-toolbar">
        <input className="Admin-search" placeholder="Поиск по никнейму / uid" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} />
        <button className="Admin-btn" onClick={load}>Найти</button>
      </div>
      {msg && <div className="Admin-msg">{msg}</div>}
      {loading ? <div className="Admin-loading">Загрузка...</div> : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr>
              <th>Никнейм</th><th>Монеты</th><th>Рейтинг</th><th>Уровень</th>
              <th>Игр</th><th>Последний вход</th><th>Статус</th><th></th>
            </tr></thead>
            <tbody>{users.map(u => (
              <tr key={u.id} className={u.banned ? "Admin-row-banned" : ""}>
                <td><div className="Admin-nick">{u.nickname}</div><div className="Admin-uid">{u.id.slice(0,8)}…</div></td>
                <td>{u.coins?.toLocaleString("ru") ?? 0}</td>
                <td>{u.rating}</td>
                <td>{u.level}</td>
                <td>{u.gamesPlayed || 0}</td>
                <td>{fmtDate(u.lastSeen)}</td>
                <td>{u.banned ? <span className="Admin-badge banned">Забанен</span> : <span className="Admin-badge active">Активен</span>}</td>
                <td><button className="Admin-btn small" onClick={() => { setEditing(u); setEditCoins(String(u.coins ?? 0)); setEditRating(String(u.rating)); setEditNote(u.adminNote || ""); setMsg(""); }}>Ред.</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="Admin-modal-overlay" onClick={() => setEditing(null)}>
          <div className="Admin-modal" onClick={e => e.stopPropagation()}>
            <div className="Admin-modal-title">Редактировать: {editing.nickname}</div>
            <label className="Admin-label">Монеты</label>
            <input className="Admin-input" type="number" value={editCoins} onChange={e => setEditCoins(e.target.value)} />
            <label className="Admin-label">Рейтинг</label>
            <input className="Admin-input" type="number" value={editRating} onChange={e => setEditRating(e.target.value)} />
            <label className="Admin-label">Заметка для команды</label>
            <textarea className="Admin-input" rows={2} value={editNote} onChange={e => setEditNote(e.target.value)} />
            <div className="Admin-label">
              <label style={{display:"flex",gap:8,alignItems:"center",cursor:"pointer"}}>
                <input type="checkbox" checked={!!editing.banned} onChange={e => setEditing({...editing, banned: e.target.checked})} />
                Заблокировать
              </label>
            </div>
            {msg && <div className="Admin-msg">{msg}</div>}
            <div className="Admin-modal-actions">
              <button className="Admin-btn" onClick={save} disabled={saving}>{saving ? "Сохранение..." : "Сохранить"}</button>
              <button className="Admin-btn secondary" onClick={() => setEditing(null)}>Отмена</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  WITHDRAWALS
// ══════════════════════════════════════════════════════════════════════════════
const WithdrawalsTab: FunctionComponent = () => {
  const [items, setItems]           = useState<Withdrawal[]>([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState("");
  const [confirm, setConfirm]       = useState<Withdrawal | null>(null);
  const [rejectTarget, setRejectTarget] = useState<Withdrawal | null>(null);
  const [saving, setSaving]         = useState(false);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminFetch(`/withdrawals?status=${statusFilter}`)); } catch (e: any) { setMsg(e.message); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const doProcess = async (status: "completed" | "rejected", refund = false) => {
    if (!confirm) return;
    setSaving(true);
    try {
      await adminFetch(`/withdrawals/${confirm.id}`, { method: "PATCH", body: JSON.stringify({ status, refund }) });
      setMsg(status === "completed"
        ? `✅ Заявка отмечена как выплаченная`
        : `✅ Заявка отклонена, монеты возвращены игроку`);
      setConfirm(null);
      load();
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  const doReject = async () => {
    if (!rejectTarget) return;
    setSaving(true);
    try {
      await adminFetch(`/withdrawals/${rejectTarget.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "rejected", refund: true }),
      });
      setMsg("✅ Заявка отклонена, монеты возвращены игроку");
      setRejectTarget(null);
      load();
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  const getRequisites = (w: Withdrawal) => {
    if (w.method === "crypto" || w.address) {
      return `${w.address} (${w.network ?? ""} ${w.currency ?? ""})`.trim();
    }
    return w.phone ?? "—";
  };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">Заявки на вывод</h2>
      <div className="Admin-toolbar">
        {["pending","completed","rejected"].map(s => (
          <button key={s} className={`Admin-btn ${statusFilter === s ? "" : "secondary"}`} onClick={() => setStatusFilter(s)}>
            {s === "pending" ? "Ожидают" : s === "completed" ? "Выполнены" : "Отклонены"}
          </button>
        ))}
      </div>
      {msg && <div className="Admin-msg">{msg}</div>}
      {loading ? <div className="Admin-loading">Загрузка...</div> : items.length === 0 ? (
        <div className="Admin-empty">Заявок нет</div>
      ) : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr><th>Игрок</th><th>Сумма</th><th>Реквизиты</th><th>Дата</th><th>Статус</th><th>Действия</th></tr></thead>
            <tbody>{items.map(w => (
              <tr key={w.id}>
                <td><div>{w.nickname}</div><div className="Admin-uid">{w.uid.slice(0,8)}…</div></td>
                <td><strong>{w.amount} ₽</strong></td>
                <td style={{fontFamily:"monospace",fontSize:"0.78rem"}}>{getRequisites(w)}</td>
                <td>{fmtDate(w.createdAt)}</td>
                <td><span className={`Admin-badge ${w.status}`}>{w.status}</span></td>
                <td>
                  {w.status === "pending" && (
                    <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                      <button className="Admin-btn small success" onClick={() => setConfirm(w)}>
                        Выплатить
                      </button>
                      <button className="Admin-btn small danger" onClick={() => setRejectTarget(w)}>
                        Отклонить
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Модалка подтверждения */}
      {confirm && (
        <div className="Admin-modal-overlay" onClick={() => !saving && setConfirm(null)}>
          <div className="Admin-modal" onClick={e => e.stopPropagation()}>
            <div className="Admin-modal-title">Подтверждение выплаты</div>

            <div style={{background:"rgba(255,200,50,0.08)",border:"1px solid rgba(255,200,50,0.2)",borderRadius:8,padding:"12px 14px",marginBottom:4}}>
              <div style={{fontSize:"0.7rem",color:"rgba(255,200,50,0.7)",marginBottom:6,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                ⚠ Выплатите деньги вручную, затем нажмите «Подтвердить»
              </div>
              <div style={{fontSize:"0.9rem",color:"#fff",marginBottom:4}}>
                Игрок: <strong>{confirm.nickname}</strong>
              </div>
              <div style={{fontSize:"1.1rem",fontWeight:800,color:"#fff",marginBottom:8}}>
                Сумма: {confirm.amount} ₽
              </div>
              <div style={{fontSize:"0.82rem",color:"rgba(255,255,255,0.6)",marginBottom:4}}>
                {confirm.method === "crypto" || confirm.address ? "Адрес крипто:" : "Телефон СБП:"}
              </div>
              <div style={{fontFamily:"monospace",fontSize:"0.88rem",color:"#fff",wordBreak:"break-all"}}>
                {getRequisites(confirm)}
              </div>
            </div>

            {msg && <div className="Admin-msg">{msg}</div>}
            <div className="Admin-modal-actions">
              <button className="Admin-btn success" onClick={() => doProcess("completed")} disabled={saving}>
                {saving ? "..." : "✓ Деньги отправлены — подтвердить"}
              </button>
              <button className="Admin-btn danger" onClick={() => doProcess("rejected", true)} disabled={saving}>
                Отклонить и вернуть монеты
              </button>
              <button className="Admin-btn secondary" onClick={() => setConfirm(null)} disabled={saving}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модалка отклонения заявки */}
      {rejectTarget && (
        <div className="Admin-modal-overlay" onClick={() => !saving && setRejectTarget(null)}>
          <div className="Admin-modal" onClick={e => e.stopPropagation()}>
            <div className="Admin-modal-title">Отклонить заявку?</div>
            <div style={{background:"rgba(220,80,80,0.08)",border:"1px solid rgba(220,80,80,0.25)",borderRadius:8,padding:"12px 14px",marginBottom:4}}>
              <div style={{fontSize:"0.9rem",color:"#fff",marginBottom:4}}>
                Игрок: <strong>{rejectTarget.nickname}</strong>
              </div>
              <div style={{fontSize:"1.05rem",fontWeight:800,color:"#fff",marginBottom:8}}>
                Сумма: {rejectTarget.amount} ₽
              </div>
              <div style={{fontSize:"0.78rem",color:"rgba(255,255,255,0.65)"}}>
                Монеты в размере {rejectTarget.amount} ₽ вернутся на баланс игрока.
              </div>
            </div>
            {msg && <div className="Admin-msg">{msg}</div>}
            <div className="Admin-modal-actions">
              <button className="Admin-btn danger" onClick={doReject} disabled={saving}>
                {saving ? "..." : "Отклонить и вернуть монеты"}
              </button>
              <button className="Admin-btn secondary" onClick={() => setRejectTarget(null)} disabled={saving}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  PAYMENTS
// ══════════════════════════════════════════════════════════════════════════════
const PaymentsTab: FunctionComponent = () => {
  const [items, setItems] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setLoading(true);
    adminFetch("/payments?limit=100").then(setItems).catch((e: any) => setMsg(e.message)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">История пополнений</h2>
      {msg && <div className="Admin-msg">{msg}</div>}
      {loading ? <div className="Admin-loading">Загрузка...</div> : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr><th>ID транзакции</th><th>Игрок</th><th>Монеты / ₽</th><th>Дата</th></tr></thead>
            <tbody>{items.map(p => (
              <tr key={p.id}>
                <td className="Admin-uid">{p.id.slice(0,12)}…</td>
                <td>{p.nickname}</td>
                <td>+{p.coins} ₽</td>
                <td>{fmtDate(p.processedAt)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  CONFIG
// ══════════════════════════════════════════════════════════════════════════════
const ConfigTab: FunctionComponent = () => {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setLoading(true);
    adminFetch("/config").then(setCfg).catch((e: any) => setMsg(e.message)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setMsg("");
    try { await adminFetch("/config", { method: "PATCH", body: JSON.stringify(cfg) }); setMsg("✅ Сохранено"); }
    catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  if (loading || !cfg) return <div className="Admin-loading">Загрузка...</div>;

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">Конфигурация платформы</h2>
      {msg && <div className="Admin-msg">{msg}</div>}
      <div className="Admin-config-grid">
        {([
          { key: "commission",    label: "Комиссия (0.0–1.0)",   hint: "0.20 = 20%" },
          { key: "minWithdraw",   label: "Мин. вывод (₽)",       hint: "500" },
          { key: "maxBet",        label: "Макс. ставка (₽)",     hint: "1000" },
          { key: "startingCoins", label: "Стартовые монеты",      hint: "0" },
          { key: "defaultRating", label: "Стартовый рейтинг",     hint: "500" },
          { key: "kFactor",       label: "ELO K-фактор",          hint: "32" },
        ] as const).map(({ key, label, hint }) => (
          <div key={key} className="Admin-config-row">
            <label className="Admin-label">{label}</label>
            <input
              className="Admin-input"
              type="number"
              step={key === "commission" ? 0.01 : 1}
              value={(cfg as any)[key]}
              placeholder={hint}
              onChange={e => setCfg({ ...cfg, [key]: parseFloat(e.target.value) })}
            />
          </div>
        ))}
        <div className="Admin-config-row wide">
          <label className="Admin-label">Пресеты ставок (через запятую)</label>
          <input
            className="Admin-input"
            value={cfg.betOptions.join(",")}
            onChange={e => setCfg({ ...cfg, betOptions: e.target.value.split(",").map(v => parseInt(v.trim(), 10)).filter(n => !isNaN(n)) })}
          />
        </div>
      </div>
      <button className="Admin-btn" onClick={save} disabled={saving}>{saving ? "Сохранение..." : "Сохранить конфиг"}</button>

      {/* Контакты поддержки */}
      <h3 style={{color:"#fff",marginTop:28,marginBottom:12,fontSize:"0.95rem",fontWeight:700}}>Контакты поддержки</h3>
      <ContactsForm />
    </div>
  );
};

const ContactsForm: FunctionComponent = () => {
  const [telegram, setTelegram] = useState("");
  const [email, setEmail]       = useState("");
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState("");

  useEffect(() => {
    adminFetch("/contacts").then((d: any) => {
      setTelegram(d.telegram || ""); setEmail(d.email || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await adminFetch("/contacts", { method: "PATCH", body: JSON.stringify({ telegram, email }) });
      setMsg("Контакты сохранены");
    } catch (e: any) { setMsg("Ошибка: " + e.message); }
    setSaving(false);
  };

  return (
    <div className="Admin-config-grid">
      <div className="Admin-config-row">
        <label className="Admin-label">Telegram (полная ссылка)</label>
        <input className="Admin-input" value={telegram} onChange={e => setTelegram(e.target.value)} placeholder="https://t.me/nardy_support" />
      </div>
      <div className="Admin-config-row">
        <label className="Admin-label">Email поддержки</label>
        <input className="Admin-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="support@nardy.io" />
      </div>
      {msg && <div className="Admin-msg" style={{gridColumn:"1/-1"}}>{msg}</div>}
      <div style={{gridColumn:"1/-1"}}>
        <button className="Admin-btn" onClick={save} disabled={saving}>{saving ? "Сохранение..." : "Сохранить контакты"}</button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  SUPPORT TICKETS
// ══════════════════════════════════════════════════════════════════════════════
const TicketsTab: FunctionComponent = () => {
  const [items, setItems] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => {
    setLoading(true);
    adminFetch("/tickets?limit=100").then(setItems).catch((e: any) => setMsg(e.message)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const resolve = async (status: string) => {
    if (!selected) return;
    setSaving(true);
    try {
      await adminFetch(`/tickets/${selected.id}`, { method: "PATCH", body: JSON.stringify({ status, adminReply: reply || undefined }) });
      setMsg("✅ Обновлено"); setSelected(null); load();
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  const statusLabel: Record<string,string> = { open: "Открыт", in_progress: "В работе", resolved: "Решён" };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">Тикеты поддержки</h2>
      {msg && <div className="Admin-msg">{msg}</div>}
      {loading ? <div className="Admin-loading">Загрузка...</div> : items.length === 0 ? (
        <div className="Admin-empty">Тикетов нет</div>
      ) : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr><th>Игрок</th><th>Тема</th><th>Статус</th><th>Дата</th><th></th></tr></thead>
            <tbody>{items.map(t => (
              <tr key={t.id}>
                <td>{t.nickname || t.uid.slice(0,8)}</td>
                <td>{t.subject}</td>
                <td><span className={`Admin-badge ${t.status}`}>{statusLabel[t.status] || t.status}</span></td>
                <td>{fmtDate(t.createdAt)}</td>
                <td><button className="Admin-btn small" onClick={() => { setSelected(t); setReply(t.adminReply || ""); setMsg(""); }}>Открыть</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="Admin-modal-overlay" onClick={() => setSelected(null)}>
          <div className="Admin-modal" onClick={e => e.stopPropagation()}>
            <div className="Admin-modal-title">{selected.subject}</div>
            <div className="Admin-ticket-body">{selected.body}</div>
            <label className="Admin-label">Ответ</label>
            <textarea className="Admin-input" rows={4} value={reply} onChange={e => setReply(e.target.value)} placeholder="Ответ игроку..." />
            {msg && <div className="Admin-msg">{msg}</div>}
            <div className="Admin-modal-actions">
              <button className="Admin-btn" onClick={() => resolve("in_progress")} disabled={saving}>В работе</button>
              <button className="Admin-btn success" onClick={() => resolve("resolved")} disabled={saving}>Решить</button>
              <button className="Admin-btn secondary" onClick={() => setSelected(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  API KEYS
// ══════════════════════════════════════════════════════════════════════════════
const KEY_FIELDS = [
  { key: "UNOPAY_API_KEY",    label: "UnoPay API Key",          hint: "Ключ от unopay.cc" },
  { key: "UNOPAY_BASE_URL",   label: "UnoPay Base URL",         hint: "https://unopay.cc" },
  { key: "HELEKET_API_KEY",   label: "Heleket API Key (приём)", hint: "Из кабинета heleket.com" },
  { key: "HELEKET_PAYOUT_KEY",label: "Heleket Payout Key",      hint: "Для выплат" },
  { key: "HELEKET_MERCHANT",  label: "Heleket Merchant UUID",   hint: "UUID мерчанта" },
  { key: "RESEND_API_KEY",    label: "Resend API Key",          hint: "re_..." },
  { key: "ADMIN_TOKEN",       label: "Admin Static Token",      hint: "Дополнительная защита /admin" },
];

// ══════════════════════════════════════════════════════════════════════════════
//  GAMES HISTORY
// ══════════════════════════════════════════════════════════════════════════════
interface GameRecord {
  id: string; winnerNick: string; loserNick: string;
  betAmount: number; isGammon: boolean; isBackgammon: boolean;
  isForfeit: boolean; isMatchWin: boolean; ratingDelta: number; endedAt: any;
}

const GamesTab: FunctionComponent = () => {
  const [items, setItems] = useState<GameRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    setLoading(true);
    adminFetch("/games?limit=200").then(setItems).catch((e: any) => setMsg(e.message)).finally(() => setLoading(false));
  }, []);

  const gameType = (g: GameRecord) => {
    if (g.isBackgammon) return "Бэкгаммон";
    if (g.isGammon)     return "Гаммон";
    if (g.isForfeit)    return "Сдача";
    return "Обычная";
  };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">История игр</h2>
      {msg && <div className="Admin-msg">{msg}</div>}
      {loading ? <div className="Admin-loading">Загрузка...</div> : items.length === 0 ? (
        <div className="Admin-empty">Игр пока нет</div>
      ) : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr>
              <th>Победитель</th><th>Проигравший</th><th>Тип</th>
              <th>Ставка</th><th>Рейтинг Δ</th><th>Дата</th>
            </tr></thead>
            <tbody>{items.map(g => (
              <tr key={g.id}>
                <td><strong style={{color:"#81c784"}}>{g.winnerNick ?? "—"}</strong></td>
                <td>{g.loserNick ?? "—"}</td>
                <td><span className="Admin-badge active">{gameType(g)}</span></td>
                <td>{g.betAmount > 0 ? `${g.betAmount} ₽` : "—"}</td>
                <td style={{color:"#81c784"}}>+{g.ratingDelta}</td>
                <td>{fmtDate(g.endedAt)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  NEWS
// ══════════════════════════════════════════════════════════════════════════════
interface NewsItem { id: string; date: string; title: string; body: string; }

const NewsAdminTab: FunctionComponent = () => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ date: "", title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/news");
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) { setMsg("Ошибка: " + e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) {
      setMsg("Заполните заголовок и текст"); return;
    }
    setSaving(true); setMsg("");
    try {
      await adminFetch("/news", {
        method: "POST",
        body: JSON.stringify({
          date: form.date,
          title: form.title,
          body: form.body,
        }),
      });
      setForm({ date: "", title: "", body: "" });
      setMsg("✅ Новость добавлена");
      load();
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  const remove = async (id: string) => {
    if (!window.confirm("Удалить новость?")) return;
    try {
      await adminFetch(`/news/${id}`, { method: "DELETE" });
      setMsg("Удалено"); load();
    } catch (e: any) { setMsg("❌ " + e.message); }
  };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">Новости</h2>
      {msg && <div className="Admin-msg">{msg}</div>}

      <div className="Admin-config-grid">
        <div className="Admin-config-row wide">
          <label className="Admin-label">Дата (напр. «Апрель 2026»)</label>
          <input className="Admin-input" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} placeholder="Оставьте пустым для автозаполнения" />
        </div>
        <div className="Admin-config-row wide">
          <label className="Admin-label">Заголовок</label>
          <input className="Admin-input" value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} />
        </div>
        <div className="Admin-config-row wide">
          <label className="Admin-label">Текст</label>
          <textarea className="Admin-input" rows={3} value={form.body} onChange={e => setForm(f => ({...f, body: e.target.value}))} />
        </div>
      </div>
      <button className="Admin-btn" onClick={save} disabled={saving} style={{marginBottom:20}}>
        {saving ? "Сохранение..." : "Добавить новость"}
      </button>

      {loading ? <div className="Admin-loading">Загрузка...</div> : items.length === 0 ? (
        <div className="Admin-empty">Новостей нет</div>
      ) : (
        <div className="Admin-table-wrap">
          <table className="Admin-table">
            <thead><tr><th>Дата</th><th>Заголовок</th><th>Текст</th><th></th></tr></thead>
            <tbody>{items.map(n => (
              <tr key={n.id}>
                <td style={{whiteSpace:"nowrap"}}>{n.date}</td>
                <td>{n.title}</td>
                <td style={{maxWidth:300,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{n.body}</td>
                <td><button className="Admin-btn small danger" onClick={() => remove(n.id)}>Удалить</button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const KeysTab: FunctionComponent = () => {
  const [vals, setVals] = useState<Record<string,string>>({});
  const [msg, setMsg]   = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      await adminFetch("/config/keys", { method: "PATCH", body: JSON.stringify(vals) });
      setMsg("✅ Ключи сохранены. Перезапусти payment-контейнер чтобы изменения вступили в силу.");
      setVals({});
    } catch (e: any) { setMsg("❌ " + e.message); }
    setSaving(false);
  };

  return (
    <div className="Admin-section">
      <h2 className="Admin-section-title">API Ключи</h2>
      <div className="Admin-msg" style={{marginBottom:16,opacity:0.6}}>
        Оставь поле пустым — значение не изменится. Изменения применятся после перезапуска сервера.
      </div>
      <div className="Admin-config-grid">
        {KEY_FIELDS.map(f => (
          <div key={f.key} className="Admin-config-row">
            <label className="Admin-label">{f.label}</label>
            <input
              className="Admin-input"
              type="password"
              autoComplete="off"
              placeholder={`Текущее значение скрыто · ${f.hint}`}
              value={vals[f.key] || ""}
              onChange={e => setVals(v => ({ ...v, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      {msg && <div className="Admin-msg">{msg}</div>}
      <button className="Admin-btn" onClick={save} disabled={saving || Object.values(vals).every(v => !v)}>
        {saving ? "Сохранение..." : "Сохранить ключи"}
      </button>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN ADMIN PANEL
// ══════════════════════════════════════════════════════════════════════════════
const AdminPanel: FunctionComponent = () => {
  const [tab, setTab]           = useState<Tab>("dashboard");
  const [stats, setStats]       = useState<Stats | null>(null);
  const [checkState, setCheck]  = useState<"loading" | "ok" | "denied">("loading");
  const profile  = useAppSelector(s => s.user.profile);
  const navigate = useNavigate();

  useEffect(() => {
    const verify = async () => {
      const user = getAuth().currentUser;
      if (!user) { setCheck("denied"); return; }
      // Проверяем Firestore admins/{uid} до любых запросов
      try {
        const snap = await getDoc(doc(getDB(), "admins", user.uid));
        if (!snap.exists()) { setCheck("denied"); return; }
      } catch { setCheck("denied"); return; }
      setCheck("ok");
      adminFetch("/stats").then(setStats).catch(() => {});
    };
    verify();
  }, []);

  if (checkState === "loading") {
    return (
      <div className="Admin-denied">
        <div className="Admin-denied-icon">⏳</div>
        <div className="Admin-denied-title">Проверка прав доступа...</div>
      </div>
    );
  }

  if (checkState === "denied") {
    return (
      <div className="Admin-denied">
        <div className="Admin-denied-icon">🔒</div>
        <div className="Admin-denied-title">Доступ запрещён</div>
        <div className="Admin-denied-sub">Этот раздел только для администраторов.</div>
        <div className="Admin-denied-sub" style={{fontSize:"0.72rem",opacity:0.4}}>UID: {profile?.uid}</div>
        <button
          style={{marginTop:16,padding:"10px 24px",background:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer"}}
          onClick={() => navigate("/")}
        >← В меню</button>
      </div>
    );
  }

  const tabs: { id: Tab; label: string; icon: string }[] = [
    { id: "dashboard",   label: "Обзор",      icon: "◈" },
    { id: "users",       label: "Игроки",     icon: "◉" },
    { id: "withdrawals", label: "Выводы",     icon: "₽" },
    { id: "payments",    label: "Платежи",    icon: "⬆" },
    { id: "games",       label: "Игры",       icon: "◈" },
    { id: "config",      label: "Настройки",  icon: "⚙" },
    { id: "tickets",     label: "Поддержка",  icon: "✉" },
    { id: "news",        label: "Новости",    icon: "📰" },
    { id: "keys",        label: "API Ключи",  icon: "🔑" },
  ];

  return (
    <div className="Admin-layout">
      <nav className="Admin-nav">
        <div className="Admin-nav-title">Нарды · Admin</div>
        {tabs.map(t => (
          <button
            key={t.id}
            className={`Admin-nav-item ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            <span className="Admin-nav-icon">{t.icon}</span>
            {t.label}
            {t.id === "withdrawals" && stats?.pendingWithdraws ? (
              <span className="Admin-nav-badge">{stats.pendingWithdraws}</span>
            ) : null}
          </button>
        ))}
        <div className="Admin-nav-user">{profile?.nickname}</div>
      </nav>
      <main className="Admin-content">
        {tab === "dashboard"   && <Dashboard stats={stats} />}
        {tab === "users"       && <UsersTab />}
        {tab === "withdrawals" && <WithdrawalsTab />}
        {tab === "payments"    && <PaymentsTab />}
        {tab === "config"      && <ConfigTab />}
        {tab === "games"       && <GamesTab />}
        {tab === "tickets"     && <TicketsTab />}
        {tab === "news"        && <NewsAdminTab />}
        {tab === "keys"        && <KeysTab />}
      </main>
    </div>
  );
};

export default AdminPanel;
